import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, endBefore, startAfter, getDocs, limitToLast } from 'firebase/firestore';
import { firestore, searchClient } from '../../firebase';
import {
  Typography, TextField, InputAdornment, IconButton, Box, Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ItemModal from "./detail";
import ItemList from "./ItemList";
import SEO from "../../components/seo";

const DataList = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [lastDoc, setLastDoc] = useState(null);
  const [firstDoc, setFirstDoc] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isLastPage, setIsLastPage] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const pageSize = 5;

  useEffect(() => {
    fetchData(page, search);
  }, []);

  const fetchData = async (pageNumber = 1, searchQuery = '', direction = 'next') => {
    setLoading(true);
    const collectionRef = collection(firestore, 'items');
    let q = query(collectionRef, limit(pageSize), orderBy('createdAt', 'desc'));

    if (searchQuery) {
      const initIndex = searchClient.initIndex('items');
      const result = await initIndex.search(searchQuery, {
        page: pageNumber - 1,
        hitsPerPage: pageSize,
      });
      if (result.hits.length === 0) {
        setData([]);
        setIsLastPage(true);
        setLoading(false);
        return;
      }
      q = query(q, where("__name__", 'in', result.hits.map(hit => hit.objectID)));
    }

    if (direction === 'next' && lastDoc && !searchQuery) {
      q = query(q, startAfter(lastDoc));
    } else if (direction === 'prev' && firstDoc && !searchQuery) {
      q = query(q, endBefore(firstDoc), limitToLast(pageSize));
    }

    try {
      const querySnapshot = await getDocs(q);
      const documents = querySnapshot.docs.map(doc => ({...doc.data(), id: doc.id}));

      setFirstDoc(querySnapshot.docs[0]);
      setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setData(documents);
      setIsLastPage(documents.length < pageSize);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearch = () => {
    setPage(1);
    setLastDoc(null);
    setFirstDoc(null);
    fetchData(1, search);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePrevClick = () => {
    if (page > 1) {
      setPage(page - 1);
      fetchData(page - 1, search, 'prev');
    }
  };

  const handleNextClick = () => {
    if (!isLastPage) {
      setPage(page + 1);
      fetchData(page + 1, search, 'next');
    }
  };

  const handleClickOpen = (item) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const handleDelete = (deletedItemId) => {
    setData(prevData => prevData.filter(item => item.id !== deletedItemId));
  };

  const itemSellCompleted = (id) => {
    setData(data.map(i => {
      if (i.id === id) {
        i.completed = true;
      }
      return i;
    }));
  };

  const formatGold = (gold) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(gold);
  };

  return (
    <Box sx={{ p: 2, mt: 2 }}>
      <SEO
        title="디아블로 아이템 거래소 - 쉽고 빠른 아이템 거래"
        description="디아블로 아이템을 쉽고 빠르게 거래하세요. 안전하고 편리한 거래 플랫폼에서 원하는 아이템을 찾아보세요."
        keywords="디아블로, 아이템 거래, 게임 아이템, 온라인 거래, 디아블로 장비"
      />
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search Items"
        value={search}
        onChange={handleSearchChange}
        onKeyDown={handleSearchKeyPress}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleSearch}>
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      <Paper elevation={3}>
        <ItemList
          data={data}
          loading={loading}
          handleClickOpen={handleClickOpen}
          pageSize={pageSize}
          formatGold={formatGold}
        />
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <IconButton onClick={handlePrevClick} disabled={page === 1 || loading}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body1">
          Page {page}
        </Typography>
        <IconButton onClick={handleNextClick} disabled={isLastPage || loading}>
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      {selectedItem && (
        <ItemModal
          open={open}
          handleClose={handleClose}
          handleComplete={itemSellCompleted}
          handleDelete={handleDelete}
          item={selectedItem}
        />
      )}
    </Box>
  );
};

export default DataList;
